import { Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { AuthHttpInterceptor, AuthService } from '@auth0/auth0-angular';
import { callbackErrorFailure } from './auth0-callback-error';
import { CallbackErrorAwareAuthService } from './callback-error-aware-auth.service';
import {
  Observable,
  catchError,
  switchMap,
  take,
  throwError,
  timeout,
} from 'rxjs';
import {
  TOKEN_ACQUIRE_BOUND_MS,
  TokenAcquireError,
  boundTokenAcquire,
  classifyAuth0AcquireFailure,
} from './token-acquire-bound';

/**
 * Marks a request already retried once after a silent token refresh so a
 * session that cannot be recovered can never spin an infinite
 * 401 -> refresh -> 401 loop. Same guard the mobile Auth0 interceptor uses.
 * Shared by both issuer branches of {@link IssuerAwareAuthInterceptor}.
 */
export const AUTH_RETRY_HEADER = 'x-ups-auth-retry';

/**
 * What the Auth0 branch needs from the owning interceptor: the injector (the
 * stock `AuthHttpInterceptor` and `AuthService` are resolved lazily, exactly as
 * before the extraction) and the latched interactive redirect, which stays on
 * the interceptor because its `redirectInFlight` latch is shared with the
 * Entra branch.
 */
export interface Auth0BranchContext {
  injector: Injector;
  triggerRedirect: () => void;
}

/**
 * Auth0 path of {@link IssuerAwareAuthInterceptor} (#31536, renew-first
 * #32089), extracted to its own file to keep the interceptor under the 500-line
 * gate. Delegate to the stock Auth0 `AuthHttpInterceptor` (allowedList +
 * token-attach stay byte-identical), with a bounded acquire and two recoveries.
 *
 * The acquire runs under {@link boundTokenAcquire} (#32087): the bound covers
 * only the acquire (disarmed once the request is issued), so a hung silent
 * renewal fails fast instead of hanging the request unsent. A non-HTTP error
 * IS the acquire (network failures arrive as `HttpErrorResponse`);
 * {@link classifyAuth0AcquireFailure} labels it:
 *
 * - `session-expired`: no token can be minted silently, so trigger the latched
 *   interactive redirect and surface the classified error.
 * - `timeout`: TRANSIENT (a blip or lock starvation is not a dead session), so
 *   mirror the Entra branch: proceed unauthenticated and let the API's 401
 *   drive ONE forced refresh. If that refresh reports the session dead, OR
 *   times out as well (auth0-spa-js dedupes concurrent `getTokenSilently`
 *   calls, so the `ignoreCache` refresh typically JOINS the still-pending
 *   stalled acquire and stalls with it), escalate to the interactive redirect:
 *   two consecutive timeouts are no longer a blip.
 * - anything else (unrecognised): surfaced untouched, never redirected on.
 *
 * On a 401 after a token WAS attached (#31536) the same forced refresh runs,
 * but a refresh TIMEOUT there surfaces the original 401 without redirecting:
 * it is a single stall of a fresh acquire, not a second consecutive one.
 */
export function interceptAuth0(
  req: HttpRequest<unknown>,
  next: HttpHandler,
  ctx: Auth0BranchContext
): Observable<HttpEvent<unknown>> {
  // Bug 37976 (review of PR 8235): while a refused Auth0 login is held, fail before any token acquire. This branch
  // gets AuthHttpInterceptor from the injector, so the HTTP_AUTH_INTERCEPTOR wrapper never runs here; without this
  // check each API call would run a silent /authorize against the refused session.
  const held = ctx.injector
    .get(CallbackErrorAwareAuthService, null)
    ?.callbackError();
  if (held) return throwError(() => callbackErrorFailure(held));
  const auth0Interceptor = ctx.injector.get(AuthHttpInterceptor);
  return boundTokenAcquire(
    req,
    next,
    (request, handler) => auth0Interceptor.intercept(request, handler),
    TOKEN_ACQUIRE_BOUND_MS,
    () => new TokenAcquireError('auth0', 'timeout')
  ).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        return retryOnceOn401(req, next, err, ctx, false);
      }
      const classified = classifyAuth0AcquireFailure(err);
      if (!(classified instanceof TokenAcquireError)) {
        // Unrecognised acquire error: never masked, never redirected on.
        return throwError(() => classified);
      }
      if (classified.kind === 'session-expired') {
        // No token can be minted silently: only an interactive sign-in
        // recovers. Latched so concurrent failures fire one redirect.
        ctx.triggerRedirect();
        return throwError(() => classified);
      }
      if (classified.kind === 'timeout') {
        // #32089 renew-first: proceed unauthenticated (mirror interceptEntra)
        // so the API's 401 drives the bounded ignoreCache refresh + replay.
        // The unauthenticated request to an allowedList URL is the accepted
        // tradeoff, same as the Entra path.
        console.warn(
          `[auth] silent Auth0 token acquisition timed out (interceptor bound ${TOKEN_ACQUIRE_BOUND_MS}ms, or the SDK's own timeout); request proceeds unauthenticated`,
          classified
        );
        return next
          .handle(req)
          .pipe(
            catchError((httpErr: unknown) =>
              httpErr instanceof HttpErrorResponse
                ? retryOnceOn401(req, next, httpErr, ctx, true)
                : throwError(() => httpErr)
            )
          );
      }
      // A TokenAcquireError kind this branch does not know: surface unchanged.
      return throwError(() => classified);
    })
  );
}

/**
 * The shared 401 gate: only an un-retried 401 enters the forced refresh; any
 * other HTTP error (or a 401 on the replay itself) surfaces unchanged.
 */
function retryOnceOn401(
  req: HttpRequest<unknown>,
  next: HttpHandler,
  err: HttpErrorResponse,
  ctx: Auth0BranchContext,
  escalateOnTimeout: boolean
): Observable<HttpEvent<unknown>> {
  const alreadyRetried = req.headers.get(AUTH_RETRY_HEADER) === '1';
  if (alreadyRetried || err.status !== 401) {
    return throwError(() => err);
  }
  return forceRefreshAuth0AndRetry(req, next, err, ctx, escalateOnTimeout);
}

/**
 * Auth0 mirror of the Entra forced refresh. On a 401 (a stale attached token,
 * or a timeout-transient request sent unauthenticated) force a NON-cached
 * silent acquire (`ignoreCache: true` is load-bearing — a plain call returns
 * the token that just 401'd) and replay once through the stock interceptor so
 * the fresh token is attached. The acquire is bounded (#32087). The ORIGINAL
 * 401 always surfaces on failure; the retry header caps this at one attempt,
 * so a dead session can never loop. Escalation to interactive re-login:
 *
 * - a refresh reporting `session-expired`: always (renewal is impossible);
 * - a refresh `timeout`: only when `escalateOnTimeout` (the timeout-transient
 *   path, where it is the SECOND consecutive acquire timeout, #32089);
 * - a replay HTTP error: never. It is an API answer, not an acquire failure,
 *   so it is not classified (a 400 whose body is `'login_required'` must not
 *   be misread as a dead session).
 */
function forceRefreshAuth0AndRetry(
  req: HttpRequest<unknown>,
  next: HttpHandler,
  originalErr: HttpErrorResponse,
  ctx: Auth0BranchContext,
  escalateOnTimeout: boolean
): Observable<HttpEvent<unknown>> {
  return ctx.injector
    .get(AuthService)
    .getAccessTokenSilently({ ignoreCache: true })
    .pipe(
      timeout({
        first: TOKEN_ACQUIRE_BOUND_MS,
        with: () => throwError(() => new TokenAcquireError('auth0', 'timeout')),
      }),
      take(1),
      switchMap(() => {
        const retried = req.clone({
          setHeaders: { [AUTH_RETRY_HEADER]: '1' },
        });
        return ctx.injector.get(AuthHttpInterceptor).intercept(retried, next);
      }),
      catchError((refreshErr: unknown) => {
        if (refreshErr instanceof HttpErrorResponse) {
          return throwError(() => originalErr);
        }
        const classified = classifyAuth0AcquireFailure(refreshErr);
        if (classified instanceof TokenAcquireError) {
          if (classified.kind === 'session-expired') {
            ctx.triggerRedirect();
          } else if (classified.kind === 'timeout' && escalateOnTimeout) {
            console.warn(
              `[auth] Auth0 token renewal timed out again after an acquire timeout (bound ${TOKEN_ACQUIRE_BOUND_MS}ms); starting interactive re-authentication`,
              classified
            );
            ctx.triggerRedirect();
          }
        }
        return throwError(() => originalErr);
      })
    );
}
